import { useEffect, useMemo, useState } from "react";
import { Database, CheckCircle, Loader2, Search } from "lucide-react";
import { toast } from "react-toastify";
import {
  getDBConfigById,
  getCategorizedTables,
  saveSelectedTables,
  exploreTables,
  
} from "../../../apiWithDBConfig";
import { Button } from "../../ui/Button.tsx";
import { Input } from "../../ui/Input.tsx";
import { useIngestionJobs } from "../ingestion/IngestionJobsContext";
import { IngestionQueueStrip, IngestionStatusBadge } from "../ingestion/IngestionFeedback";

export default function DBWorkspace({ configId }) {
  const [config, setConfig] = useState(null);
  const [selectedTables, setSelectedTables] = useState([]);
  const [unselectedTables, setUnselectedTables] = useState([]);
  const [initialSelected, setInitialSelected] = useState([]);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { registerJob, getJob, retryJob } = useIngestionJobs({
    sourceDbId: configId,
    resourceTypes: ["metadata_sync"],
  });

  /* ================= LOAD DATA ================= */

  useEffect(() => {
    if (!configId) return;

    const load = async () => {
      try {
        setLoading(true);

        const [{ data: cfg }, { data: categorized }] = await Promise.all([
          getDBConfigById(configId),
          getCategorizedTables(configId),
        ]);

        setConfig(cfg);

        const hasTables =
          (categorized.selected_tables?.length || 0) +
            (categorized.unselected_tables?.length || 0) >
          0;

        if (!hasTables) {
          const { data: allTables } = await exploreTables(configId);
          setSelectedTables([]);
          setUnselectedTables(allTables || []);
          setInitialSelected([]);
        } else {
          setSelectedTables(categorized.selected_tables || []);
          setUnselectedTables(categorized.unselected_tables || []);
          setInitialSelected(categorized.selected_tables || []);
        }
      } catch {
        toast.error("Failed to load database tables");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [configId]);

  /* ================= FILTER ================= */

  const allTables = useMemo(() => {
    return [...new Set([...selectedTables, ...unselectedTables])].filter((t) =>
      t.toLowerCase().includes(search.toLowerCase())
    );
  }, [selectedTables, unselectedTables, search]);

  const isDirty =
    JSON.stringify([...selectedTables].sort()) !==
    JSON.stringify([...initialSelected].sort());

  /* ================= ACTIONS ================= */

  const toggleTable = (table) => {
    if (selectedTables.includes(table)) {
      setSelectedTables((p) => p.filter((t) => t !== table));
      setUnselectedTables((p) => [...p, table]);
    } else {
      setSelectedTables((p) => [...p, table]);
      setUnselectedTables((p) => p.filter((t) => t !== table));
    }
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const { data } = await saveSelectedTables(configId, selectedTables);
      if (data?.ingestion) {
        registerJob({
          summary: data.ingestion,
          sourceDbId: configId,
          resourceId: configId,
          label: `${config.database_name} catalog`,
        });
      }
      toast.success("Tables saved successfully");
      setInitialSelected(selectedTables);
    } catch (error) {
      toast.error("Failed to save tables");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  /* ================= UI ================= */

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!config) {
    
    return null;
  }

  return (
    <div className=" ml-4 mr-4 flex flex-col h-full">
      <IngestionQueueStrip
        className="mb-4"
        sourceDbId={configId}
        resourceTypes={["metadata_sync"]}
      />
      {/* HEADER + SEARCH */}
    <div className="flex flex-wrap items-center gap-3 mb-4">
    <div className="flex items-center gap-2 shrink-0">
     <Database className="w-5 h-5 text-primary" />
     <h2 className="text-lg font-semibold">{config.database_name}</h2>
     <span className="text-xs text-muted-foreground">({config.db_type})</span>
     <IngestionStatusBadge
       compact
       job={getJob("metadata_sync", configId)}
       onRetry={() => retryJob(getJob("metadata_sync", configId)?.jobId)}
     />
    </div>
    <div className="relative flex-1 min-w-[160px]">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input
       className="pl-9 w-full"
       placeholder="Search tables..."
       value={search}
       onChange={(e) => setSearch(e.target.value)}
     />
     </div>
     </div>
      {/* TABLE LIST */}
      <div className="no-scrollbar flex-1 overflow-y-auto border border-border rounded-md">
  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-0">
  {allTables.map((t) => {
    const selected = selectedTables.includes(t);
    return (
      <label
        key={t}
        className={`flex items-center gap-3 px-4 py-2 text-sm cursor-pointer border-b border-border
          ${
            selected
              ? "bg-primary/10 text-primary"
              : "hover:bg-muted/40"
          }`}
      >
              <input
                type="checkbox"
                checked={selected}
                onChange={() => toggleTable(t)}
                className="accent-primary"
              />
              <span className="truncate">{t}</span>
            </label>
          );
        })}
        </div>
      </div>

      {/* FOOTER */}
      <div className="pt-4  mt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-muted-foreground">
            {selectedTables.length} of {selectedTables.length + unselectedTables.length} tables selected
          </div>
        </div>

          <div className="flex justify-end">
            <Button
              disabled={!selectedTables.length || !isDirty || saving}
              onClick={handleSave}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Save Selected Tables
            </Button>
          </div>
      </div>
    </div>
  );
}
